import { useCallback, useMemo } from 'react';

import { useSupportTicketsInfiniteQuery } from '../shared/apis';
import type { SupportModule } from '../shared/schemas/support-query-schema';

type UseSupportTicketsInfiniteProps = {
  module: SupportModule;
  freshdeskId: string;
  sort?: string;
  per_page?: number;
  filters?: string;
  search?: string;
};

export const useSupportTicketsInfinite = ({
  module,
  freshdeskId,
  sort = '-updated_at',
  per_page = 25,
  filters = '',
  search = '',
}: UseSupportTicketsInfiniteProps) => {
  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetching: isLoading,
  } = useSupportTicketsInfiniteQuery({ module, freshdeskId, sort, per_page, filters, search });

  const tickets = useMemo(() => data.pages.flatMap((page) => page.data), [data]);

  const totalCount = useMemo(() => data.pages[0]?.meta?.total ?? 0, [data]);

  const fetchMore = useCallback(() => {
    if (hasNextPage && !isLoading) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isLoading]);

  return {
    tickets,
    totalCount,
    hasMore: hasNextPage,
    isLoading,
    loadedPages: data.pageParams as number[],
    fetchMore,
  };
};
